let audioContext: AudioContext | null = null;

/**
 * Gets or creates the AudioContext instance.
 * AudioContext is lazily initialised to comply with browser autoplay policies.
 */
function getAudioContext(): AudioContext | null {
    if (typeof window === 'undefined') {
        return null;
    }
    if (!audioContext) {
        audioContext = new window.AudioContext();
    }
    return audioContext;
}

/**
 * Plays the notification sound using the given AudioContext.
 * Plays two blips in quick succession (G4 then C5) similar to LinkedIn's notification.
 */
function playSound(context: AudioContext, volume: number) {
    const currentTime = context.currentTime;

    // First blip - G4 (392 Hz).
    const oscillator1 = context.createOscillator();
    oscillator1.type = 'sine';
    oscillator1.frequency.setValueAtTime(392, currentTime); // G4 note

    const gainNode1 = context.createGain();
    gainNode1.gain.setValueAtTime(0, currentTime);
    gainNode1.gain.linearRampToValueAtTime(volume, currentTime + 0.01); // Quick attack
    gainNode1.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.08); // Quick decay

    oscillator1.connect(gainNode1);
    gainNode1.connect(context.destination);

    oscillator1.start(currentTime);
    oscillator1.stop(currentTime + 0.08);

    // Second blip - C5 (523 Hz), slightly delayed.
    const oscillator2 = context.createOscillator();
    oscillator2.type = 'sine';
    oscillator2.frequency.setValueAtTime(523, currentTime + 0.1); // C5 note

    const gainNode2 = context.createGain();
    gainNode2.gain.setValueAtTime(0, currentTime + 0.1);
    gainNode2.gain.linearRampToValueAtTime(volume, currentTime + 0.11); // Quick attack
    gainNode2.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.18); // Quick decay

    oscillator2.connect(gainNode2);
    gainNode2.connect(context.destination);

    oscillator2.start(currentTime + 0.1);
    oscillator2.stop(currentTime + 0.18);
}

/**
 * Plays a notification "blip" sound when a new message is received.
 * @param volume Volume level from 0 to 1 (default: 0.2)
 */
export async function playNotificationSound(volume: number = 0.2) {
    const context = getAudioContext();
    if (!context) {
        return;
    }
    // Always resume the context before playing to ensure it's ready.
    await context.resume();
    playSound(context, volume);
}
